import React from "react";
import Modal from "react-modal";
import { Field, Form, Formik } from "formik";
import { Oval } from "react-loader-spinner";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  toggleShowEditVehicleModal,
  updateVehicle,
  setSelectedVehicle,
} from "../../vehiclesSlice";
import { ReactComponent as Close } from "../../../../assets/images/close.svg";
import { Vehicle } from "../../../../models";

import "./EditVehicleModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

const validationSchema = Yup.object().shape({
  label: Yup.string().required("Label is required"),
  fuelType: Yup.string()
    .oneOf(["diesel", "gas"], "Fuel type must be diesel or gas")
    .required("Fuel type is required"),
  mpg: Yup.number()
    .transform((value, original) => (original === "" ? undefined : value))
    .typeError("MPG must be a number")
    .positive("MPG must be greater than 0")
    .max(100, "MPG must be 100 or less")
    .optional(),
});

export type EditVehicleFormModel = Vehicle;

export function EditVehicleModal() {
  const dispatch = useAppDispatch();

  const selectedVehicle = useAppSelector(
    (state) => state.vehicles.selectedVehicle
  );
  const isOpen = useAppSelector((state) => state.vehicles.showEditVehicleModal);
  const updateVehicleAPI = useAppSelector(
    (state) => state.vehicles.updateVehicle
  );

  React.useEffect(() => {
    if (!updateVehicleAPI.loading && !!updateVehicleAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (
      !updateVehicleAPI.loading &&
      !updateVehicleAPI.error &&
      updateVehicleAPI.value
    ) {
      toast.success("Successfully updated vehicle.");
      dispatch(toggleShowEditVehicleModal());
      dispatch(setSelectedVehicle(undefined));
    }
  }, [
    updateVehicleAPI.loading,
    !!updateVehicleAPI.error,
    !!updateVehicleAPI.value,
    dispatch,
  ]);

  const toggleModal = () => {
    dispatch(toggleShowEditVehicleModal());
    dispatch(setSelectedVehicle(undefined));
  };

  const onSubmit = (form: EditVehicleFormModel) => {
    // The MPG number field yields "" when cleared; coerce to a real number or
    // undefined so the denormalizer sends null rather than an empty string.
    const mpg =
      form.mpg === undefined || (form.mpg as unknown) === ""
        ? undefined
        : Number(form.mpg);
    dispatch(updateVehicle({ ...form, mpg }));
  };

  if (!selectedVehicle) return null;

  const initialValues: EditVehicleFormModel = {
    ...selectedVehicle,
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={toggleModal} style={modalStyles}>
      <div className="update-vehicle-modal-component">
        <Close className="close-button" onClick={toggleModal} />

        <div className="title">Update Vehicle</div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, touched }) => (
            <Form className="update-vehicle-form">
              <div className="field-container">
                <div className="label">Label:</div>
                <Field id="label" name="label" className="text-input" />
                {errors.label && touched.label && (
                  <div className="form-error">{errors.label}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Fuel Type:</div>
                <Field
                  as="select"
                  id="fuelType"
                  name="fuelType"
                  className="text-input"
                >
                  <option value="diesel">Diesel</option>
                  <option value="gas">Gas</option>
                </Field>
                {errors.fuelType && touched.fuelType && (
                  <div className="form-error">{errors.fuelType}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">MPG:</div>
                <Field
                  id="mpg"
                  name="mpg"
                  type="number"
                  step="0.1"
                  min="0"
                  className="text-input"
                />
                {errors.mpg && touched.mpg && (
                  <div className="form-error">{errors.mpg}</div>
                )}
              </div>

              <button
                type="submit"
                className="add-button"
                disabled={updateVehicleAPI.loading}
              >
                Update Vehicle
                {updateVehicleAPI.loading && (
                  <div style={{ marginLeft: 8 }}>
                    <Oval
                      color="#131313"
                      height={20}
                      width={20}
                      secondaryColor="#FBD20A"
                    />
                  </div>
                )}
              </button>

              {!!updateVehicleAPI.error && (
                <div className="error-text">
                  Something went wrong. Please try again.
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
